import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import AlertBar from '../core/display/AlertBar';
import AssignmentPhaseIcon from '../core/display/AssignmentPhaseIcon/AssignmentPhaseIcon';
import TutorialModal from './TutorialModal';
import { selectAssignment, selectUser } from '../../store/selectors';
import { conditionallyPlural, getPhaseStatus } from '../../utils/functions';
import { PhaseCode } from '../../types/types';

type AssignmentStep = { phase: PhaseCode; description: string };

interface Props {
  buttonMode?: boolean;
  buttonText?: string;
}

function StudentAssignmentTutorial({ buttonMode = false, buttonText = '' }: Props): JSX.Element {
  const user = useSelector(selectUser);
  const assignment = useSelector(selectAssignment);
  const userId = user.actingAs ? 'null' : user.userId;
  const assignmentId = assignment?.assignmentId ?? 'null';

  const listOfSteps = useMemo(() => {
    const listItems: AssignmentStep[] = [];
    if (assignment) {
      const phases = getPhaseStatus(assignment);
      if (phases.groupFormation)
        listItems.push({ phase: 'groupFormation', description: `Complete your group formation survey` });
      if (assignment.groupsEnabled && assignment.allowStudentGroupManagement)
        listItems.push({ phase: 'groupFormation', description: `Form groups with your peers` });
      if (phases.submission) listItems.push({ phase: 'submit', description: `Upload your submission` });
      if (phases.review) {
        const submitter = assignment.instructorUpload ? `instructor's` : `peers'`;
        listItems.push({ phase: 'review', description: `Review your ${submitter} submissions` });
      }
      if (phases.feedback)
        listItems.push({ phase: 'feedback', description: `Provide feedback to the peers who reviewed your work` });
      if (phases.evaluate)
        listItems.push({ phase: 'evaluate', description: `Evaluate the performance of your group members` });
      if (phases.reflection) listItems.push({ phase: 'reflection', description: `Reflect on your results` });
    }
    return listItems;
  }, [assignment]);

  return (
    <TutorialModal
      id={`student-assignment-tutorial-for-${userId}-in-${assignmentId}`}
      heading="Assignment Tasks"
      checkToDismiss={!buttonMode}
      buttonMode={buttonMode}
      buttonText={buttonText}
    >
      <div id="student-assignment-tutorial">
        <p>
          In this assignment you&apos;ll participate in <b>{conditionallyPlural(listOfSteps.length, 'step')}</b>:
        </p>
        <ol>
          {listOfSteps.map((step) => (
            <li key={step.phase}>
              <AssignmentPhaseIcon phase={step.phase} />
              {step.description}
            </li>
          ))}
        </ol>
        <AlertBar backgroundColor="#ffefd2">
          Your overall grade is based on completing all of your required steps thoughtfully and accurately.
        </AlertBar>
      </div>
    </TutorialModal>
  );
}

export default StudentAssignmentTutorial;
